import React, {useEffect, useState} from "react";
import {inject, observer} from 'mobx-react';
import MultiSelectSort from "../../components/sortable-select";
import {fetch} from "../../utils/api-wrapper";
import FilterInputField from "../../components/filter-input-field";
import PaginationControls from "../../components/pagination-controls";
import Select from "react-select";
import Checkbox from "../../components/checkbox";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faArrowDown,
    faArrowUp,
    faExclamationTriangle,
    faPencilAlt,
    faPlus,
    faSpinner, faTimes,
    faWindowClose
} from "@fortawesome/free-solid-svg-icons";
import HoverInfoCard from "../../components/hover-card";
import Modal from "../../components/modal";
import { downloadDepartmentImportTemplateCsv } from "../../utils/department-import-csv-template";
import ImportResultModal from "../../components/import-result-modal";

const DEPARTMENT_IMPORT_SUMMARY_SPEC = [
    { key: "departments_created", labelKey: "departments_import_summary_created" },
    // "departments_updated" is what this screen has always read. The real
    // service summary key is "existing_department_rows" — this mismatch is
    // pre-existing and tracked separately as XPEN-3301. Do not "fix" it here.
    { key: "departments_updated", labelKey: "departments_import_summary_updated" },
    { key: "memberships_upserted", labelKey: "departments_import_summary_memberships" },
    { key: "rows_failed", labelKey: "departments_import_summary_failed" },
];

export const isDepartmentValid = (department) => {
    if (!department || !department.name?.trim()) {
        return false;
    }
    for (const user of (department.users || [])) {
        if (!user || !user.id_user) return false;
        if (!Array.isArray(user.functions) || user.functions.length === 0) return false;
        for (const func of user.functions) {
            if (!func || !func.name?.trim()) return false;
        }
    }
    return true;
};

const AdminDepartments = ({data}) => {
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterValue, setFilterValue] = useState("");
    const [sorting, setSorting] = useState([{"id": "name", "desc": false}]);
    const [departments, setDepartments] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRowCount, setTotalRowCount] = useState(0);
    const [client, setClient] = useState(null);
    const [clients, setClients] = useState(null);
    const [showNewDepartmentModal, setShowNewDepartmentModal] = useState(false);
    const [departmentsDeleteSelection, setDepartmentsDeleteSelection] = useState([]);
    const [showUpdateDepartmentModal, setShowUpdateDepartmentModal] = useState(false);
    const [showDeleteDepartmentModal, setShowDeleteDepartmentModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importLoading, setImportLoading] = useState(false);
    const [departmentImportResult, setDepartmentImportResult] = useState(null);
    const [updatedDepartmentData, setUpdatedDepartmentData] = useState({
        _id: null,
        id_client: "",
        name: "",
        users: [],
    });
    const [newDepartment, setNewDepartment] = useState({
        _id: null,
        id_client: "",
        name: "",
        users: [],
    });

    const [users, setUsers] = useState([]);

    const functions = [
        {name: "ceo"},
        {name: "cto"},
        {name: "lawyer"},
        {name: "employee"},
        {name: "cfo"},
        {name: "accountant"},
        {name: "coo"},
        {name: "director_of_finance"},
        {name: "director_of_technology"},
        {name: "managing_partner"},
    ]


// Map user object to react-select option
    const functionMap = fonction => ({
        value: fonction.name,
        label: data.t.read(fonction.name),
    });

    const handleAdjointsReorder = (newOptions) => {
        const newList = newOptions?.map(opt => {
            return opt.value
        });
        return newList;
    };
    const handleFunctionsReorder = (newOptions) => {
        const newList = newOptions?.map(opt => {
            return {name: opt.value}
        });
        return newList;
    };

    useEffect(() => {
        setLoading(true);
        refreshClients()
    }, []);

    useEffect(() => {
        refreshUsers()
    }, [client]);

    useEffect(() => {
        if (client) {
            setPage(1);
            refreshDepartments(client);
        }

    }, [client, pageSize, sorting, filterValue]);

    useEffect(() => {
        if (client) {
            refreshDepartments(client)
        }
    }, [page]);

    const refreshClients = () => {
        fetch("/clients/", "GET", {}, response => {
                const clients = response.data.clients;
                setClients(clients);

                setClient(clients.find(c => c._id === data.id_client)); //Set Client to the user's corresponding client
                setLoading(false);
            },
            error => {
                // Error handling
                setErrorMessage(data.t.read("failed_to_fetch_clients"));
                setLoading(false);
            }
        );
    }

    const selectClient = (client) => {
        setClient({...client})
        setPage(1)
        setFilterValue("");
        refreshDepartments(client);
    }

    const refreshDepartments = (selectClient = client) => {
        let start = (page - 1) * pageSize;
        const filters = [{id: "id_client", value: selectClient._id}];
        if (filterValue && filterValue.trim() !== "") {
            filters.push({id: "search", value: filterValue.trim()});
        }
        const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}&sorting=${encodeURIComponent(JSON.stringify(sorting ?? []))}&start=${encodeURIComponent(start)}&page_size=${pageSize}`;
        fetch(`/departments/${searchData}`, "GET", {},
            response => {
                let fetchedDepartments = response.data.departments;
                setDepartments([...fetchedDepartments]);
                setTotalRowCount(response.data.meta.totalRowCount)
                setLoading(false);
            });
    }

    const refreshUsers = (selectedClient = client) => {
        const client_id = selectedClient?._id;
        fetch(`/client/${client_id}/usersDetail`, "GET", {}, response => {
            let fetchedUsers = response.data;
            setUsers([...fetchedUsers]);
        });
    }

    const processDepartmentImport = () => {
        if (!importFile) return;
        const lowerName = (importFile.name || "").toLowerCase();
        const formData = new FormData();
        if (lowerName.endsWith(".xlsx")) {
            formData.append("excel", importFile);
        } else if (lowerName.endsWith(".csv")) {
            formData.append("csv", importFile);
        } else {
            setErrorMessage(data.t.read("departments_import_invalid_file"));
            return;
        }
        setImportLoading(true);
        fetch("/departments/import", "POST", formData,
            (response) => {
                // HTTP 200 and 207 both land here (both are 2xx)
                const result = response?.data ?? {};
                setShowImportModal(false);
                setImportFile(null);
                setImportLoading(false);
                setDepartmentImportResult(result);
                refreshDepartments(client);
            },
            (e) => {
                setShowImportModal(false);
                setImportFile(null);
                setImportLoading(false);
                const result = e?.response?.data ?? {};
                if (result.summary || result.errors?.length > 0 || result.alerts?.length > 0) {
                    setDepartmentImportResult(result);
                } else {
                    setErrorMessage(data.t.read("departments_import_failed"));
                }
            },
            true // multipart FormData
        );
    };

    // Helper mapping function for displaying a user in a Select.
    const userMap = (user) => {
        return {
            value: user,
            label: `${user.first_name} ${user.last_name}`,
        };
    };

    // Form submission handler
    const updateDepartmentSubmit = (e) => {
        e.preventDefault();
        setShowUpdateDepartmentModal(false)
        updateDepartment()
    };


    const updateDepartment = (departmentId = updatedDepartmentData._id?.$oid, updatedDepartment = updatedDepartmentData) => {
        const updatedDepartmentWithoutId = {...updatedDepartment}; // Shallow copy of updatedDepartment
        delete updatedDepartmentWithoutId._id; // Remove the _id field

        updatedDepartmentWithoutId.users = updatedDepartmentWithoutId.users.map((user) => {
            let functions = user.functions;
            return {
                id_user: user.id_user,
                functions: functions,
            }

        })
        fetch(`/departments/${departmentId}`, "PUT", updatedDepartmentWithoutId,
            () => {
                refreshDepartments(client);
                setSuccessMessage(data.t.read("department_updated_successfully"))
            },
            () => {
                setErrorMessage(data.t.read("failed_to_update_department"))
                setLoading(false);
            }
        );
    };

    const openUpdateDepartmentModal = (selectedDepartment) => {
        setUpdatedDepartmentData(selectedDepartment)
        setShowUpdateDepartmentModal(true)
    }

    const cancelUpdateDepartmentModal = () => {
        setShowUpdateDepartmentModal(false);
    }

    const createDepartment = (departmentParam = newDepartment) => {
        // Create form data to send the department data in 'request.form'
        let department = {...departmentParam}
        department.users = department.users.map((user) => {
            let functions = user.functions;
            return {
                id_user: user.id_user,
                functions: functions,
            }

        })
        fetch("/departments/", "POST", department,
            () => {
                setSuccessMessage(data.t.read("department_created_successfully"));
                refreshDepartments(client);
            },
            () => {
                alert("Error");
                setLoading(false);
            }
        );
    };

    const newDepartmentSubmit = (e) => {
        e.preventDefault();
        setShowNewDepartmentModal(false)
        createDepartment();
    };

    const openNewDepartmentModal = () => {
        setNewDepartment({id_client: client._id, users: [], name: ""})
        setShowNewDepartmentModal(true);
    }

    const cancelNewDepartmentModal = () => {
        setShowNewDepartmentModal(false);
    }

    const deleteDepartments = () => {
        fetch("/departments/", "DELETE", {"department_ids": departmentsDeleteSelection},
            () => {
                refreshDepartments();
                setDepartmentsDeleteSelection([])
            },
            (error) => {
                setErrorMessage(data.t.read("failed_to_delete"))
                setLoading(false);
            }
        );
    }


    const confirmDeleteDepartment = () => {
        deleteDepartments()
        setShowDeleteDepartmentModal(false);
    }

    const openDeleteDepartmentModal = () => {
        setShowDeleteDepartmentModal(true);
    }

    const cancelDeleteDepartmentModal = () => {
        setShowDeleteDepartmentModal(false);
        setDepartmentsDeleteSelection([])
    }

    const isAllDepartmentsSelected = () => {
        if (!departments || !departments.length) {
            return false;
        }
        return departments.every(department => departmentsDeleteSelection.includes(department._id?.$oid));
    }

    const isDepartmentSelected = (department) => {
        return departmentsDeleteSelection.includes(department._id?.$oid);
    }

    const toggleAllDepartmentsSelected = () => {
        setDepartmentsDeleteSelection(isAllDepartmentsSelected() ?
            departmentsDeleteSelection.filter(id => !departments.some(department => department._id?.$oid === id))
            :
            [...departmentsDeleteSelection, ...departments.map((department) => department._id?.$oid).filter((id) => !departmentsDeleteSelection.includes(id))]
        );
    }

    const sortTable = (name) => {
        const sorter = sorting.find(s => s.id === name)
        if (sorter === undefined)
            return setSorting([{"id": name, "desc": true}])

        if (sorter.desc)
            return setSorting([{"id": name, "desc": false}])
        else
            return setSorting([])
    }

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (rows) => {
        setPage(1)
        setPageSize(rows)
    }

    //pagination logic
    const indexOfFirstItem = (page - 1) * pageSize;
    const indexOfLastItem = indexOfFirstItem + departments.length - 1;
    const totalPages = Math.ceil(totalRowCount / pageSize);
    const emptyRowsCount = Math.max(0, pageSize - departments.length);
    const emptyRows = Array(emptyRowsCount).fill({});

    // Common styles for react-select components.
    const styleSelect = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            border: "0 !important",
            boxShadow: state.isFocused ? "0px 0px 10px #F39800 " : "0 !important"
        })
    };

    const userStyleSelect = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            boxShadow: "none",
            backgroundColor: "transparent",
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
    };


    return !loading ?
                    <div>
                        <div>
                            <div className='pagination-row'>
                                <FilterInputField
                                    filterValue={filterValue} setFilterValue={setFilterValue} placeholder={""}>
                                </FilterInputField>
                                <PaginationControls
                                    page={page}
                                    totalPages={totalPages}
                                    indexOfFirstItem={indexOfFirstItem}
                                    indexOfLastItem={indexOfLastItem}
                                    handlePageChange={handlePageChange}
                                    totalItems={totalRowCount}
                                    data={data}
                                    handleRowsPerPageChange={handleRowsPerPageChange}
                                    rowsPerPage={pageSize}
                                ></PaginationControls>
                            </div>
                            <div className="d-flex justify-content-between align-items-center px-4 py-2">
                                <div className="flex mr-3">
                                    <Select
                                        name="client"
                                        isDisabled={data.role !== "SuperAdmin"}
                                        value={client === null ? null : {value: client, label: client.name}}
                                        options={clients.map(client => ({value: client, label: client.name}))}
                                        styles={styleSelect}
                                        classNamePrefix="select"
                                        onChange={(choice) => {
                                            selectClient(choice.value);
                                        }}
                                    />
                                </div>
                                <div className="d-flex">
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm ml-2"
                                        disabled={departmentsDeleteSelection.length === 0}
                                        onClick={() => setShowDeleteDepartmentModal(true)}>
                                        {data.t.read("delete") + " (" + departmentsDeleteSelection.length + ")"}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm ml-2"
                                        onClick={() => setShowImportModal(true)}>
                                        {data.t.read("departments_import_button")}
                                    </button>
                                    <button
                                        className='btn btn-primary btn-sm ml-2 justify-content-end'
                                        type="button"
                                        onClick={openNewDepartmentModal}>
                                        {data.t.read("add")}
                                    </button>
                                </div>
                            </div>
                            <div className="mb-2 card rounded-lg admin-table">
                                <table className='table table-hover font-small'>
                                    <thead>
                                    <tr>
                                        <th>
                                            <Checkbox disabled={!departments || !departments.length}
                                                      isChecked={isAllDepartmentsSelected()}
                                                      onChange={toggleAllDepartmentsSelected}/>
                                        </th>
                                        <th className="pointer col-4" onClick={() => sortTable("name")}>
                                            {data.t.read("name")}
                                            {sorting.find(s => s.id === "name") &&
                                                <FontAwesomeIcon
                                                    className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                                    icon={sorting.find(s => s.id === "name").desc ? faArrowDown : faArrowUp}/>}
                                        </th>
                                        <th className="pointer col-10" onClick={() => sortTable("users")}>
                                            {data.t.read("users")}
                                            {sorting.find(s => s.id === "users") &&
                                                <FontAwesomeIcon
                                                    className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                                    icon={sorting.find(s => s.id === "users").desc ? faArrowDown : faArrowUp}/>}
                                        </th>
                                        <th className="pointer col-1">
                                            Actions
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {
                                        departments.map((department) => {
                                            return <tr key={department._id?.$oid}>
                                                <td>
                                                    <Checkbox isChecked={isDepartmentSelected(department)}
                                                              onChange={() => isDepartmentSelected(department) ? setDepartmentsDeleteSelection(departmentsDeleteSelection.filter(x => x !== department._id?.$oid)) : setDepartmentsDeleteSelection([...departmentsDeleteSelection, department._id?.$oid])}/>
                                                </td>
                                                <td onClick={() => openUpdateDepartmentModal(department)}
                                                    className="col-2">
                                                    {department.name}
                                                </td>
                                                <td className=" ml-3"
                                                    onClick={() => openUpdateDepartmentModal(department)}>
                                                    <HoverInfoCard
                                                        info={
                                                            department.users
                                                                ? department.users
                                                                    .map(user => {
                                                                        const match = users.find(u => u.id_user === user.id_user);
                                                                        return match ? userMap(match).label : '';
                                                                    })
                                                                    .filter(Boolean)
                                                                    .join(', ')
                                                                : ''
                                                        }
                                                    />
                                                </td>
                                                <td className="col-1">
                                                    {<FontAwesomeIcon className="mr-2" icon={faPencilAlt}
                                                                      onClick={() => openUpdateDepartmentModal(department)}
                                                                      type="button"/>}
                                                    {<FontAwesomeIcon onClick={() => {
                                                        setDepartmentsDeleteSelection([department._id?.$oid])
                                                        openDeleteDepartmentModal()
                                                    }}
                                                                      icon={faWindowClose} type="button"
                                                                      style={{color: 'red'}}/>}
                                                </td>


                                            </tr>
                                        })
                                    }
                                    {
                                        emptyRows.map((_, idx) => (
                                            <tr key={`empty-${idx}`}>
                                                <td colSpan="7">&nbsp;</td>
                                            </tr>
                                        ))
                                    }
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {
                            showUpdateDepartmentModal && (
                                <Modal title={data.t.read("departments")}>
                                    <form onSubmit={updateDepartmentSubmit}>
                                        <div className={"form-group"}>
                                            <label htmlFor="name">{data.t.read("department_name")}</label>
                                            <input type="text" className="form-control" name="department name"
                                                   value={updatedDepartmentData.name}
                                                   onChange={(event) => setUpdatedDepartmentData({
                                                       ...updatedDepartmentData,
                                                       name: event.target.value
                                                   })} required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="users">
                                                {data.t.read("users")}:
                                                <button className='btn btn-primary btn-xs ml-2 pl-1 pr-1' type="button"
                                                        onClick={() => setUpdatedDepartmentData({
                                                            ...updatedDepartmentData,
                                                            users: [...updatedDepartmentData.users, {}]
                                                        })}
                                                >
                                                    <FontAwesomeIcon icon={faPlus}/>
                                                </button>
                                            </label>
                                            {updatedDepartmentData.users.map((user, i) => (
                                                <div key={i} className={"border p-2 mb-4 d-flex"}>
                                                    <div className={"col-11"}>
                                                        <div className="d-flex  align-items-center mb-2">
                                                            <h5 className={"mr-2 col-3"}>{data.t.read("user")}</h5>
                                                            <div className="col-9">
                                                                <Select
                                                                    menuPortalTarget={document.body}
                                                                    required
                                                                    styles={userStyleSelect}
                                                                    name="users"
                                                                    value={users.filter((u) => u.id_user === user.id_user).map(userMap) ?? null}
                                                                    options={users.filter(
                                                                        u => !updatedDepartmentData.users?.some(
                                                                            existing => existing.id_user === u.id_user
                                                                        )).map(userMap)}
                                                                    onChange={(choice) => {
                                                                        const new_user = { ...choice.value };

                                                                        const original_user = updatedDepartmentData.users[i];
                                                                        new_user.adjoints = (original_user?.adjoints || []).filter(
                                                                            (u) => u.id_user !== new_user.id_user
                                                                        );
                                                                        new_user.functions = [...(original_user?.functions || [])];

                                                                        setUpdatedDepartmentData((prev) => ({...prev, users: prev.users.map((u, idx) => (idx === i ? new_user : u)),}));}}
                                                                    placeholder={data.t.read("users")}
                                                                    classNamePrefix="select"

                                                                />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div className="d-flex  align-items-center">
                                                                <h5 className={"mr-2 col-3"}>{data.t.read("functions")}</h5>
                                                                <div className={"col-9"}>
                                                                    <MultiSelectSort
                                                                        options={functions.map(functionMap)}
                                                                        value={user.functions?.map(functionMap)}
                                                                        selected={user.functions?.map(functionMap)}
                                                                        className={"border-0"}
                                                                        setSelected={(newList) => {
                                                                            let list = handleFunctionsReorder(newList);
                                                                            user.functions = list;
                                                                            let users = updatedDepartmentData.users
                                                                            users[i].functions = list
                                                                            users = updatedDepartmentData.users.map((user, index) => (index === i ? {...user, functions:list} : user))
                                                                            setUpdatedDepartmentData({
                                                                                ...updatedDepartmentData,
                                                                                users: users
                                                                            });
                                                                        }

                                                                        }
                                                                    />
                                                                </div>

                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className={"d-flex col-2 align-items-center"}>

                                                        <FontAwesomeIcon icon={faTimes} type="button" color={"red"}
                                                                         onClick={() => setUpdatedDepartmentData({
                                                                             ...updatedDepartmentData,
                                                                             users: updatedDepartmentData.users.filter((x, index) => index !== i)
                                                                         })}
                                                        />
                                                    </div>

                                                </div>

                                            ))}
                                        </div>
                                        <span className="d-flex justify-content-between">
                                        <button type="button" className={"btn btn-outline-dark"}
                                                onClick={cancelUpdateDepartmentModal}>
                                            {data.t.read("cancel")}
                                        </button>
                                        <button type="submit" className="btn btn-primary" disabled={!isDepartmentValid(updatedDepartmentData)}>
                                             {data.t.read("save")}
                                        </button>
                                    </span>

                                    </form>
                                </Modal>
                            )
                        }
                        {
                            showNewDepartmentModal && (
                                <Modal title={data.t.read("new_department")}>
                                    <form onSubmit={newDepartmentSubmit}>
                                        <div className={"form-group"}>
                                            <label htmlFor="name">{data.t.read("department_name")}</label>
                                            <input type="text" className="form-control" name="department name"
                                                   value={newDepartment.name}
                                                   onChange={(event) => setNewDepartment({
                                                       ...newDepartment,
                                                       name: event.target.value
                                                   })} required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="users">
                                                {data.t.read("users")}:
                                                <button className='btn btn-primary btn-xs ml-2 pl-1 pr-1' type="button"
                                                        onClick={() => setNewDepartment({
                                                            ...newDepartment,
                                                            users: [...newDepartment.users, {}]
                                                        })}
                                                >
                                                    <FontAwesomeIcon icon={faPlus}/>
                                                </button>
                                            </label>
                                            {newDepartment.users.map((user, i) => (
                                                <div key={i} className={"border p-2 mb-4 d-flex"}>
                                                    <div className={"col-11"}>
                                                        <div className="d-flex  align-items-center mb-2">
                                                            <h5 className={"mr-2 col-3"}>{data.t.read("user")}</h5>
                                                            <div className="col-9">
                                                                <Select
                                                                    menuPortalTarget={document.body}
                                                                    required
                                                                    styles={userStyleSelect}
                                                                    name="users"
                                                                    value={users.filter((u) => u.id_user === user.id_user).map(userMap) ?? null}
                                                                    options={users.filter(
                                                                        u => !newDepartment.users?.some(
                                                                            existing => existing.id_user === u.id_user
                                                                        )).map(userMap)}
                                                                    onChange={(choice) => {
                                                                        const new_user = { ...choice.value };

                                                                        const original_user = newDepartment.users[i];
                                                                        new_user.adjoints = (original_user?.adjoints || []).filter(
                                                                            (u) => u.id_user !== new_user.id_user
                                                                        );
                                                                        new_user.functions = [...(original_user?.functions || [])];

                                                                        setNewDepartment((prev) => ({...prev, users: prev.users.map((u, idx) => (idx === i ? new_user : u)),}));}}
                                                                    placeholder={data.t.read("users")}
                                                                    classNamePrefix="select"

                                                                />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div className="d-flex  align-items-center">
                                                                <h5 className={"mr-2 col-3"}>{data.t.read("functions")}</h5>
                                                                <div className={"col-9"}>
                                                                    <MultiSelectSort
                                                                        options={functions.map(functionMap)}
                                                                        value={user.functions?.map(functionMap)}
                                                                        selected={user.functions?.map(functionMap)}
                                                                        className={"border-0"}
                                                                        setSelected={(newList) => {
                                                                            let list = handleFunctionsReorder(newList);
                                                                            user.functions = list;
                                                                            let users = newDepartment.users
                                                                            users[i].functions = list
                                                                            users = newDepartment.users.map((user, index) => (index === i ? {...user, functions:list} : user))
                                                                            setNewDepartment({
                                                                                ...newDepartment,
                                                                                users: users
                                                                            });
                                                                        }

                                                                        }
                                                                    />
                                                                </div>

                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className={"d-flex col-2 align-items-center"}>

                                                        <FontAwesomeIcon icon={faTimes} type="button" color={"red"}
                                                                         onClick={() => setNewDepartment({
                                                                             ...newDepartment,
                                                                             users: newDepartment.users.filter((x, index) => index !== i)
                                                                         })}
                                                        />
                                                    </div>

                                                </div>

                                            ))}
                                        </div>                                        <span className="d-flex justify-content-between">
                                        <button type="button" className={"btn btn-outline-dark"}
                                                onClick={cancelNewDepartmentModal}>
                                            {data.t.read("cancel")}
                                        </button>
                                        <button type="submit" className="btn btn-primary" disabled={!isDepartmentValid(newDepartment)}>
                                             {data.t.read("add")}
                                        </button>
                                    </span>
                                    </form>
                                </Modal>
                            )
                        }

                        {showDeleteDepartmentModal && (
                            <Modal
                                title={data.t.read("department_deletion")}
                                okAction={confirmDeleteDepartment}
                                cancelAction={cancelDeleteDepartmentModal}
                                okText={data.t.read("delete")}
                                cancelText={data.t.read("cancel")}>
                                <p className='text-danger'>{data.t.read("delete")}?</p>
                            </Modal>
                        )}
                        {showImportModal && (
                            <Modal
                                width="50vw"
                                title={data.t.read("departments_import_modal_title") || "Import Departments"}
                                okText={data.t.read("departments_import_button") || "Import"}
                                okAction={processDepartmentImport}
                                invalid={importLoading}
                                cancelText={data.t.read("cancel")}
                                cancelAction={() => { setShowImportModal(false); setImportFile(null); }}>
                                <div>
                                    <div className="card p-2 mb-2">
                                        <p className="mb-2">{data.t.read("departments_import_help") || "Upload a CSV or Excel file. Each row can create or update a department and optionally add a user membership."}</p>
                                        <button
                                            type="button"
                                            className="btn btn-link p-0"
                                            onClick={downloadDepartmentImportTemplateCsv}>
                                            {data.t.read("departments_import_template_download") || "Download CSV template"}
                                        </button>
                                    </div>
                                    <div className="alert alert-warning py-2 mb-2" role="alert">
                                        <div className="d-flex align-items-center mb-1" style={{ fontWeight: 600 }}>
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                                            {data.t.read("departments_import_rules_title") || "Important — before you import"}
                                        </div>
                                        <ul className="mb-0 small pl-3">
                                            <li>{data.t.read("departments_import_rule_dept_required") || "The department column is required on every row. It is matched case-insensitively against existing departments — if no match is found, a new department is created."}</li>
                                            <li>{data.t.read("departments_import_rule_users_exist") || "Users must already be active in the client profile — they will not be created on import. Match is attempted by email first, then by first name and last name."}</li>
                                            <li>{data.t.read("departments_import_rule_email_recommended") || "Email is strongly recommended. If two users share the same name, the name-only match is ambiguous and that row's membership will be skipped."}</li>
                                            <li>{data.t.read("departments_import_rule_functions") || "Functions are optional and pipe-separated (e.g. cfo|accountant). Accepted values: ceo, cto, lawyer, employee, cfo, accountant, coo, director_of_finance, director_of_technology, managing_partner."}</li>
                                        </ul>
                                    </div>
                                    <ul className="small text-muted mb-2 pl-3">
                                        <li>{data.t.read("departments_import_column_department") || "department (required) — department name; created automatically if it does not exist"}</li>
                                        <li>{data.t.read("departments_import_column_email") || "email (optional, recommended) — matched first for user lookup; prevents ambiguity when two users share the same name"}</li>
                                        <li>{data.t.read("departments_import_column_first_name") || "first_name (optional) — used together with last_name when email is absent"}</li>
                                        <li>{data.t.read("departments_import_column_last_name") || "last_name (optional) — used together with first_name when email is absent"}</li>
                                        <li>{data.t.read("departments_import_column_functions") || "functions (optional) — pipe-separated list of roles, e.g. cfo|accountant"}</li>
                                    </ul>
                                    <input
                                        className="form-control-file"
                                        type="file"
                                        accept=".csv,.xlsx"
                                        id="import-departments-file"
                                        aria-label={data.t.read("departments_import_modal_title") || "Import Departments"}
                                        onChange={(e) => setImportFile(e.target.files[0] ?? null)}
                                    />
                                </div>
                            </Modal>
                        )}
                        {departmentImportResult && (
                            <ImportResultModal
                                data={data}
                                result={departmentImportResult}
                                summarySpec={DEPARTMENT_IMPORT_SUMMARY_SPEC}
                                onClose={() => setDepartmentImportResult(null)}
                            />
                        )}
                        {
                            errorMessage && (
                                <Modal title="Attention" okText={"OK"} okAction={() => setErrorMessage(null)}>
                                    <p style={{whiteSpace: 'pre-line'}} className='text-danger'>{errorMessage}</p>
                                </Modal>)
                        }
                        {
                            successMessage && (
                                <Modal title="Attention" okText={"OK"} okAction={() => setSuccessMessage(null)}>
                                    <p style={{whiteSpace: 'pre-line'}}>{successMessage}</p>
                                </Modal>

                            )
                        }
                    </div>
                    :
                    <div>
                        <div className="d-flex loading-container w-100">
                            <FontAwesomeIcon className="infinite-rotate" icon={faSpinner}/>
                        </div>
                    </div>
}

export default inject('data')(observer(AdminDepartments));